import React, { Fragment, useCallback, useEffect, useReducer, useRef } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import { priceToFloat } from 'helpers/ProductUtils';
import { SmallLoader } from 'components/Loader';
import {
  getLists,
  getListsForItemId,
  heartProduct,
  toggleHeartingLoginModal,
  unHeartProduct
} from 'actions/hearts';
import NewCollectionModal from 'components/account/NewCollectionModal';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';

import css from 'styles/components/account/socialCollectionsWidget.scss';

const OPEN = 'OPEN';
const SHOW_MORE = 'SHOW_MORE';
const SAVE_COLLECTION = 'SAVE_COLLECTION';
const ON_OPEN_MENU = 'ON_OPEN_MENU';
const SHOW_NEW_COLLECTION_MODAL = 'SHOW_NEW_COLLECTION_MODAL';
const IS_LOADING = 'IS_LOADING';

const initialState = {
  showMore: false,
  showNewCollectionModal: false,
  isLoading: false,
  hasOpened: false
};

const reducer = (state, action) => {
  const { type, showMore, showNewCollectionModal, isLoading } = action;
  switch (type) {
    case OPEN:
      return { ...state, hasOpened: true };
    case SHOW_MORE:
      return { ...state, showMore };
    case SAVE_COLLECTION:
      return { ...state, showMore: true, showNewCollectionModal: false };
    case SHOW_NEW_COLLECTION_MODAL:
      return { ...state, showNewCollectionModal };
    case IS_LOADING:
      return { ...state, isLoading };
    case ON_OPEN_MENU:
      return { ...state, showMore: true, isLoading: true };
    default:
      return initialState;
  }
};

export const SocialCollectionsWidget = ({
  canAddNewCollection = false,
  shouldAddImmediately = false,
  heartProduct,
  unHeartProduct,
  isCustomer,
  getStyleId,
  getLists,
  getListsForItemId,
  toggleHeartingLoginModal,
  hearts,
  colorId,
  productId,
  price,
  missingDimension,
  sourcePage
}) => {
  const firstList = useRef();

  const [state, dispatch] = useReducer(reducer, initialState);

  const {
    showMore,
    showNewCollectionModal,
    isLoading,
    hasOpened
  } = state;

  const itemId = getStyleId();
  const priceAsFloat = typeof price === 'string' ? priceToFloat(price) : price;

  const {
    lists,
    itemIdLists
  } = hearts;

  useEffect(() => {
    if (!showMore) {
      return;
    }

    const bindCloseEvent = e => {
      if (!e.target.closest(`[data-collection="${itemId}"]`)) {
        dispatch({ type: SHOW_MORE, showMore: false });
      }
    };

    document.body.addEventListener('click', bindCloseEvent);
    return () => document.body.removeEventListener('click', bindCloseEvent);

  }, [itemId, showMore]);

  const onGetListsForItemId = () => {
    getListsForItemId({ itemId }).then(() => {
      dispatch({ type: IS_LOADING, isLoading: false });
      firstList.current?.focus();
    });
  };

  const getHeartInfo = (listId = 'h.') => ({
    itemId,
    listId,
    colorId,
    productId,
    price: priceAsFloat,
    missingDimension,
    sourcePage
  });

  const getListDetails = () => {
    if (!lists) {
      getLists().then(onGetListsForItemId);
    } else {
      onGetListsForItemId();
    }
  };

  const onCloseMenu = () => {
    dispatch({ type: SHOW_MORE, showMore: false });
  };

  const onOpenMenu = () => {
    if (!isCustomer) {
      return toggleHeartingLoginModal(true);
    }

    if (!hasOpened) {
      dispatch({ type: OPEN });
    }

    if (shouldAddImmediately && !hasOpened) { // heart product to default list immediately when opened
      heartProduct(getHeartInfo(), getListDetails);
    } else { // otherwise, just get an updated list of collections
      getListDetails();
    }

    dispatch({ type: ON_OPEN_MENU });
  };

  const onCollectionChange = e => {
    const { checked, id } = e.currentTarget;
    dispatch({ type: IS_LOADING, isLoading: true });

    // `checked` comes back as the state it "will be" once its been clicked
    const heartType = checked ? heartProduct : unHeartProduct;
    heartType(getHeartInfo(id), onGetListsForItemId);
  };

  const onSaveNewCollection = response => {
    const { listId } = response;
    dispatch({ type: SAVE_COLLECTION });

    heartProduct(getHeartInfo(listId), () => {
      getLists().then(onGetListsForItemId);
    });
  };

  const toggleNewCollectionModal = useCallback(showNewCollectionModal => () => {
    dispatch({ type: SHOW_NEW_COLLECTION_MODAL, showNewCollectionModal });
  }, []);

  return (
    <>
      { canAddNewCollection &&
        <NewCollectionModal
          showNewCollectionModal={showNewCollectionModal}
          onCancelModal={toggleNewCollectionModal(false)}
          onDoneModal={onSaveNewCollection} />
      }

      <div className={css.container} data-collection={itemId}>
        <button type="button" className={cn(css.addToButton, { [css.basic]: shouldAddImmediately && !hasOpened })} onClick={showMore ? onCloseMenu : onOpenMenu}>Add to a Collection</button>
        {
          showMore &&
          <div className={css.lists}>
            { canAddNewCollection &&
              <button
                type="button"
                className={css.addNew}
                aria-expanded={showMore}
                onClick={toggleNewCollectionModal(true)}>
              Add to new Collection
              </button>
            }

            { (lists && itemIdLists && !isLoading)
              ? lists.map(({ listId, name }, index) => {
                const matchingList = itemIdLists.find(itemIdList => listId === itemIdList.listId);
                return (
                  <Fragment key={listId}>
                    <input
                      id={`${listId}`}
                      ref={index === 0 ? firstList : null} // focus on first list checkbox
                      onChange={onCollectionChange}
                      defaultChecked={matchingList}
                      type="checkbox" />
                    <label htmlFor={`${listId}`}>{name}</label>
                  </Fragment>
                );
              })
              : <SmallLoader />
            }
          </div>
        }
      </div>
    </>
  );
};

export const mapStateToProps = state => ({
  isCustomer: !!state.cookies['x-main'],
  hearts: state.hearts
});

const ConnectedSocialCollectionsWidget = connect(mapStateToProps, {
  getLists,
  getListsForItemId,
  heartProduct,
  unHeartProduct,
  toggleHeartingLoginModal
})(SocialCollectionsWidget);

export default withErrorBoundary(SocialCollectionsWidget.displayName, ConnectedSocialCollectionsWidget);
